import { useEffect, useState } from "react";
import { useScript } from "../utils";

type ExecuteRecaptchaType = (action: string) => Promise<string>;

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options?: { action: string }
      ) => Promise<string>;
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey?: string,
          options?: { action: string }
        ) => Promise<string>;
      };
    };
  }
}

/**
 * When using this hook on a page, make sure to display the `<RecaptchaPolicy />` component somewhere visible to the user.
 * ({@link https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed See Google's FAQ.})
 */
export const useRecaptcha = () => {
  const [execute, setExecute] = useState<ExecuteRecaptchaType>(
    () => () => Promise.reject("Recaptcha has not yet loaded")
  );
  const { status } = useScript(
    `https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`
  );

  useEffect(() => {
    if (
      status === "ready" &&
      window.grecaptcha &&
      window.grecaptcha.enterprise
    ) {
      setExecute(
        () =>
          (action = "") =>
            new Promise<string>((resolve) => {
              window.grecaptcha.enterprise.ready(() => {
                window.grecaptcha.enterprise
                  .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
                    action,
                  })
                  .then((token: string) => resolve(token));
              });
            })
      );
    }
  }, [status]);

  return { execute };
};

export default useRecaptcha;
